import "./TeamPageV2.scss";
import {SearchOutlined, UserOutlined} from "@ant-design/icons";
import {Button, Input, Skeleton, Tabs, TabsProps} from "antd";
import {useRecoilState} from "recoil";
import {memberState} from "../../../../state/atoms";
import {useCallback, useEffect, useMemo, useState} from "react";
import {MemberDto} from "../../../../generated/account-client";
import AccountsApi from "../../../../api/AccountsApi";
import {UsersTable, UsersTableType} from "./UsersTable";
import {MemberDtoPlanEnum} from "../../../../generated/dashboard-client";
import UsersApi from "../../../../api/UsersApi";
import {success} from "../../../../service/MessageService";
import {handleServerError} from "../../../../util/ErrorHandler";
import {useLocation, useNavigate} from "react-router-dom";
import {env} from "../../../../env/env";
import {debounce} from "lodash";

const {Search} = Input;

export function TeamPageV2() {
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [member] = useRecoilState(memberState);
  const [users, setUsers] = useState([] as MemberDto[]);
  const [isFreeAccount, setIsFreeAccount] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState("1");
  const [searchKey, setSearchKey] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);

  const debouncedSetSearchKey = useMemo(() => debounce((value: string) => {
    setSearchKey(value);
    setCurrentPage(1);
    setPageSize(10);
  }, 300), []);

  const fetchData = useCallback(() => {
    if (!member.id || !member.plan || !member.internalId) return;
    setLoading(true);
    console.log("=> fetching data for currentPage=", currentPage, " pageSize=", pageSize, ", searchKey=", searchKey);

    let status = "active";
    if (activeTabKey === "2") {
      status = "inactive";
    }

    AccountsApi.getUsersDetails(member.id, status, currentPage, pageSize, searchKey).then((resp) => {
      setLoading(false);
      const found = resp.data.users ?? [];
      console.log("=> found: ", found);

      if (currentPage === 1 && activeTabKey === "1") {
        setUsers([{
          email: member.email,
          name: member.name,
          role: "Owner",
          invitationStatus: "Active"
        } as MemberDto, ...found]);
      } else {
        setUsers([...found]);
      }

      setTotalUsers(resp.data.total ?? 0);

      if (member.plan === MemberDtoPlanEnum.Free) setIsFreeAccount(true);
    }).catch(() => {
      setLoading(false);
    });
  }, [member.id, member.internalId, member.plan, member.email, member.name, currentPage, pageSize, searchKey, activeTabKey]);

  useEffect(fetchData, [fetchData]);

  useEffect(() => {
    if (location.search.indexOf("pending") > -1) {
      setActiveTabKey("2");
    }
  }, [location]);

  function onMenuOptionClick(option: string, user: MemberDto) {
    if (option === "edit-locations") {
      navigate(env.dashboardPath + `/account/team/user/${user.id}/locations`);
    } else if (option === "edit-permissions") {
      navigate(env.dashboardPath + `/account/team/user/${user.id}/permissions`);
    } else if (option === "resend-invite") {
      UsersApi.sendInvitationEmail(user.id || "").then(() => {
        success("Invitation sent!");
      }).catch((e) => {
        handleServerError(e);
        setLoading(false);
      });
    } else if (option === "cancel-invite") {
      UsersApi.revokeInvitation(user.id!).then(() => {
        success("Invitation revoked!");
        setUsers(users.filter((it) => it.id !== user.id));
        setLoading(false);
      }).catch((e) => {
        handleServerError(e);
        setLoading(false);
      });
    } else if (option === "remove-access") {
      setLoading(true);
      UsersApi.removeUser(user.id!).then(() => {
        success("Invitation revoked!");
        setUsers(users.filter((it) => it.id !== user.id));
        setLoading(false);
      }).catch((e) => {
        handleServerError(e);
        setLoading(false);
      });
    }
  }

  function sortUsers(key: string) {
    setUsers([...users].sort((a: MemberDto, b: MemberDto) => {
      if (!key) return 0;
      return key === "asc" ? (a.name ?? "").localeCompare(b.name ?? "") : (b.name ?? "").localeCompare(a.name ?? "")
    }))
  }

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: "Active Users",
      children: <UsersTable
        onSortClick={sortUsers}
        onMenuOptionClick={onMenuOptionClick}
        type={UsersTableType.Active}
        users={users}
        pagination={{
          current: currentPage,
          pageSize,
          total: totalUsers,
          onChange: (page: number, pageSize: number) => {
            setCurrentPage(page);
            setPageSize(pageSize);
          }
        }}
      />
    },
    {
      key: "2",
      label: "Pending Invitations",
      children: <UsersTable
        onSortClick={sortUsers}
        onMenuOptionClick={onMenuOptionClick}
        type={UsersTableType.Pending}
        users={users}
        pagination={{
          current: currentPage,
          pageSize,
          total: totalUsers,
          onChange: (page: number, pageSize: number) => {
            setCurrentPage(page);
            setPageSize(pageSize);
          }
        }}
      />
    },
    // {
    //   key: "3",
    //   label: "Inactive Users",
    //   children: <UsersTable
    //     onSortClick={sortUsers}
    //     onMenuOptionClick={onMenuOptionClick}
    //     type={UsersTableType.Inactive}
    //     users={users.filter(it => it.invitationStatus === "Revoked")}/>
    // },
  ];

  function onNewUserClick() {
    if (isFreeAccount) {
      window.MHM.ProModal.upgrade({
        memberId: member.id,
        onSuccess: () => window.location.reload()
      });
    } else {
      navigate(env.dashboardPath + "/account/team/new");
    }
  }

  function onSearchKeyUp(target: HTMLInputElement) {
    debouncedSetSearchKey(target.value);
  }

  return (
    <div className={"content team-page-v2"}>
      <div className={"banner"}>
        <h3>My Team</h3>
        <h5>Work better together</h5>
        <div className={"users"}>
          <div className={"circle a"}><UserOutlined/></div>
          <div className={"circle b"}><UserOutlined/></div>
          <div className={"circle c"}><UserOutlined/></div>
        </div>
      </div>

      <header>
        <Button className={"new"} onClick={onNewUserClick}>+ New User</Button>
        {users.length > 0 &&
          <Search className={"search"} onKeyUp={ev => onSearchKeyUp(ev.target as HTMLInputElement)}
                  placeholder="Search users" prefix={<SearchOutlined/>} addonAfter={<></>}/>}
      </header>

      {loading && <Skeleton/>}

      {!loading && users.length > 0 && <Tabs
        activeKey={activeTabKey}
        onChange={setActiveTabKey}
        items={items}/>}
    </div>
  );
}
